import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type HTMLAttributes,
  type Ref,
} from "react";
import { Input as AntInput, Tooltip, message } from "antd";
import {
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  RightOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  useCreateChallenge,
  type CreateChallengeVariables,
} from "../../../../queries/game-pass/useCreateChallenge";
import {
  mapChallengeDraftToUpdateChallengeRequest,
  useUpdateChallenge,
} from "../../../../queries/game-pass/useUpdateChallenge";
import {
  mapChallengeDraftToUpdateChallengeRewardRequest,
  useUpdateChallengeReward,
} from "../../../../queries/game-pass/useUpdateChallengeReward";
import { useDeleteChallenge } from "../../../../queries/game-pass/useDeleteChallenge";
import {
  mapTaskDraftToCreateTaskRequest,
  useCreateTask,
} from "../../../../queries/game-pass/useCreateTask";
import {
  mapTaskDraftToUpdateTaskRequest,
  useUpdateTask,
} from "../../../../queries/game-pass/useUpdateTask";
import { useDeleteTask } from "../../../../queries/game-pass/useDeleteTask";
import { useGetGamePassConfig } from "../../../../queries/game-pass/useGetGamePassConfig";
import { useGetTaskOptions } from "../../../../queries/game-pass/useGetTaskOptions";
import {
  getBetCurrencyMinimums,
  getBetCurrencyOptions,
  getBetTypeOptions,
  getQualifierOptions,
  getRewardTypeOptions,
  getSocialTaskTypeOptions,
  getSportOptions,
  getTaskTypeOptions,
  type ChallengeRewardOption,
} from "../../../../queries/game-pass/gamePassOptions";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import {
  buildGamePassImageUrl,
  uploadGamePassImage,
  type GamePassImageFolder,
} from "../../../../utils/gamePassImageUpload";
import {
  Button,
  FileDropzone,
  Input,
  RebetLoader,
  Select,
  StatusBadge,
} from "../../../shared";

import {
  DESCRIPTION_MAX,
  createEmptyReward,
  createEmptyTask,
  duplicateTaskDraft,
  getMissingChallengeRequirements,
  isChallengeValid,
  buildDefaultRewardParams,
  humanizeParamName,
  isParamValueComplete,
  reconcileTaskReorder,
  stampTaskSubmissionModeAtSave,
  reorderTasks,
  sanitizeDecimalInput,
  sanitizeParamInput,
  type ChallengeDraft,
  type ChallengeRequirementKey,
  type RewardDraft,
  type RewardType,
  type TaskDraft,
  type TaskSubmissionModeDefaults,
} from "../createSeasonData";

import ConfirmDeleteChallengeModal from "./ConfirmDeleteChallengeModal";
import ConfirmDeleteTaskModal from "./ConfirmDeleteTaskModal";
import CreateTaskModal from "./CreateTaskModal";

import styles from "./ChallengeCard.module.css";

// Antd's TextArea lives on the AntInput namespace. We keep the AntInput
// import for that — regular text inputs use the shared `Input` so they pick
// up the canonical `custom-form-input` placeholder treatment.
const { TextArea } = AntInput;

/** Maps each required-challenge key to its on-screen label so a disabled Save
 *  button can spell out what's still missing instead of leaving the admin to
 *  guess (e.g. a legacy challenge loaded with an empty reward row). */
const CHALLENGE_REQUIREMENT_LABELS: Record<ChallengeRequirementKey, string> = {
  name: AppStrings.CHALLENGE_NAME,
  reward: AppStrings.REWARD,
};

/** Visual mode for `ChallengeCard`.
 *
 *  - `"edit"` (default) — the existing wizard / Scheduled-season editor. The
 *    collapsed view shows Duplicate / Drag / Edit icons and clicking Edit
 *    re-opens the expanded editor.
 *  - `"locked"` — read-only view used on the Season Details page for Active
 *    (and Up-Next) seasons. The card is permanently collapsed, the header
 *    icons are replaced with a "Locked" pill, and the modals (Create Task /
 *    Confirm Delete) never mount because there are no entry points to them.
 *
 *  This is strictly additive — `"edit"` is the default so every existing
 *  call site (the wizard) keeps the original behaviour. */
export type ChallengeCardMode = "edit" | "locked";

/**
 * Drag-and-drop bindings supplied by a parent that owns a
 * `@dnd-kit/sortable` `SortableContext`. When provided, the collapsed
 * card becomes a sortable item — `setNodeRef` + `transformStyle` are
 * applied to the `<article>`, `attributes` are spread on the same
 * element for a11y, and `listeners` + `setActivatorNodeRef` are wired
 * onto the drag-handle button so users can only initiate a drag from
 * the handle (not by grabbing any random part of the card).
 *
 * When omitted, the drag handle renders in its inert `disabled` state
 * exactly as it did before — so locked-mode call sites (Season
 * Details, where cards live outside any DndContext) and isolated unit
 * tests don't need to change.
 *
 * Kept as an opaque prop bundle so `ChallengeCard.tsx` doesn't have to
 * import from `@dnd-kit/*` — keeps the card dnd-agnostic and the
 * coupling lives exclusively at the `ChallengesAndTasks` adapter.
 */
export type ChallengeCardDragProps = {
  setNodeRef: Ref<HTMLElement>;
  setActivatorNodeRef: Ref<HTMLButtonElement>;
  attributes: HTMLAttributes<HTMLElement>;
  listeners: HTMLAttributes<HTMLElement> | undefined;
  transformStyle: CSSProperties;
  isDragging: boolean;
};

export type ChallengeCardProps = {
  challenge: ChallengeDraft;
  /** 1-based position. Drives the "#1", "Challenge 1" labels. */
  index: number;
  onChange: (next: ChallengeDraft) => void;
  onSave: () => void;
  onEdit: () => void;
  onClose: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  /** Defaults to `"edit"` so existing call sites are unaffected. Set to
   *  `"locked"` to render the Season Details locked variant. */
  mode?: ChallengeCardMode;
  /** Sortable bindings from a `@dnd-kit/sortable` parent. See
   *  `ChallengeCardDragProps` for the wiring contract. Omit (or pass
   *  `undefined`) to keep the drag handle inert — used by locked-mode
   *  call sites and component-isolated tests. */
  drag?: ChallengeCardDragProps;
  /** Id of the draft season this challenge belongs to. When provided
   *  AND the user creates a new task (modal in create mode), the card
   *  fires `POST /game-pass-admin/v2/admin/tasks` via `useCreateTask` and
   *  only appends the task to local state once the API confirms. When
   *  omitted (locked-mode cards in SeasonDetails before the season
   *  detail endpoint is wired, isolated unit tests) the create path
   *  falls back to local-only — preserves backwards compatibility for
   *  every existing call site. */
  seasonId?: string | null;
  /** Season-level "Same XP reward for all tasks" amount. When set (non-empty),
   *  the Create / Edit Task modal locks every task's XP Amount field to this
   *  value (disabled + pre-filled) so all tasks share one reward. Omit / empty
   *  to keep per-task XP freely editable. */
  forcedXpAmount?: string;
  /** Season-level submission defaults for new / switched task types. */
  defaultSubmissionModes?: TaskSubmissionModeDefaults;
  /** The task currently held on the season editor's in-memory clipboard, or
   *  `null` when nothing has been copied. Drives the "Paste Task" button's
   *  visibility and supplies the source draft pasted into this challenge. */
  copiedTask?: TaskDraft | null;
  /** Copies a task to the editor's shared clipboard (lifted to
   *  `ChallengesAndTasks`, backed by `sessionStorage`, so a task copied here can
   *  be pasted into another challenge in the same OR a different season). */
  onCopyTask?: (task: TaskDraft) => void;
  /** Clears the shared clipboard. Wired to the paste card's dismiss button so
   *  the admin can drop a copied task without pasting it. */
  onClearClipboard?: () => void;
};

/**
 * Fold the `img_key` resolved by a create/update back into the draft so a
 * later edit in the same session (before any reload) reflects the real
 * persisted state instead of a stale local one:
 *  - a string → the newly-stored key,
 *  - `null`   → an explicit clear (collapses to `undefined` on the draft),
 *  - `undefined` → the image wasn't touched, so keep whatever the draft had.
 */
const commitResolvedImgKey = (
  resolved: string | null | undefined,
  current: string | null | undefined,
): string | undefined =>
  resolved === undefined ? (current ?? undefined) : (resolved ?? undefined);

/**
 * Fold the `image_url` returned by a create/update response back into the
 * draft so the post-save preview (after the local `File` is dropped) renders
 * the BE-hosted image directly instead of the broken `img_key`-reconstructed
 * URL. Mirrors `commitResolvedImgKey`'s tri-state semantics:
 *  - a string → the BE's ready-to-render URL,
 *  - `null`   → the image was cleared (collapses to `undefined`),
 *  - `undefined` → the response omitted the field, so keep the draft's value.
 */
const commitResolvedImageUrl = (
  responseImageUrl: string | null | undefined,
  current: string | null | undefined,
): string | undefined =>
  responseImageUrl === undefined
    ? (current ?? undefined)
    : (responseImageUrl ?? undefined);

/**
 * Renders a single Challenge in either an editing-form state or a collapsed
 * summary state. The parent owns the canonical state; this component is
 * fully controlled and emits patched `ChallengeDraft`s back via `onChange`.
 */
const ChallengeCard: FC<ChallengeCardProps> = ({
  challenge,
  index,
  onChange,
  onSave,
  onEdit,
  onClose,
  onDelete,
  onDuplicate,
  mode = "edit",
  drag,
  seasonId,
  forcedXpAmount,
  defaultSubmissionModes,
  copiedTask = null,
  onCopyTask,
  onClearClipboard,
}) => {
  const createChallengeMutation = useCreateChallenge();
  const updateChallengeMutation = useUpdateChallenge();
  const updateChallengeRewardMutation = useUpdateChallengeReward();
  const deleteChallengeMutation = useDeleteChallenge();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const deleteTaskMutation = useDeleteTask();
  const isLocked = mode === "locked";

  // Backend-driven option catalogues for the reward dropdown (this card) and
  // the Create / Edit Task modal (forwarded below). Both queries are cached +
  // deduped by React Query, so every card sharing the editor reuses one fetch.
  // Each derivation helper falls back to the bundled static catalogue while the
  // queries load / error, so the editor never renders an empty dropdown.
  const { data: gamePassConfig } = useGetGamePassConfig();
  const { data: taskOptions } = useGetTaskOptions();

  // Cache of already-uploaded image keys, keyed by the `File` reference the
  // dropzone produced. A `File` stays referentially stable inside the draft
  // state across re-renders / repeated Saves, so this guarantees we presign +
  // PUT each picked file to S3 exactly once — re-saving a challenge after a
  // metadata-only edit (or re-opening the task editor without swapping the
  // image) reuses the existing `img_key` instead of uploading a duplicate.
  const uploadedImageKeysRef = useRef<Map<File, string>>(new Map());

  /** Resolve the `img_key` for a dropzone file: returns `undefined` when no
   *  file is attached (so the payload omits `img_key` and existing behaviour
   *  is untouched), otherwise uploads once and caches the resulting S3 key.
   *  Throws on upload failure so the caller's existing try/catch surfaces the
   *  error and keeps the modal / card open for a retry. */
  const resolveImageKey = useCallback(
    async (
      file: File | undefined,
      folder: GamePassImageFolder,
    ): Promise<string | undefined> => {
      if (!file) return undefined;
      const cached = uploadedImageKeysRef.current.get(file);
      if (cached) return cached;
      const key = await uploadGamePassImage({ file, folder });
      uploadedImageKeysRef.current.set(file, key);
      return key;
    },
    [],
  );

  /** Resolve the `img_key` to send on an UPDATE (PATCH), collapsing the three
   *  possible intents into the value the mappers expect:
   *  - a freshly-picked `file` → upload once and send the new key (string),
   *  - no new file but the persisted image was explicitly cleared
   *    (`draftImgKey === null`) → send `null` so the backend drops it,
   *  - otherwise → `undefined` so the payload omits `img_key` and the stored
   *    image is left untouched. */
  const resolveUpdateImageKey = useCallback(
    async (
      file: File | undefined,
      draftImgKey: string | null | undefined,
      folder: GamePassImageFolder,
    ): Promise<string | null | undefined> => {
      const uploaded = await resolveImageKey(file, folder);
      if (uploaded !== undefined) return uploaded;
      return draftImgKey === null ? null : undefined;
    },
    [resolveImageKey],
  );
  // Sortable wiring is only meaningful in edit mode. Locked cards in
  // SeasonDetails render OUTSIDE the DndContext, so their parent never
  // supplies `drag`; we still gate here so a future caller can't
  // accidentally make a locked card draggable.
  const sortable = !isLocked ? drag : undefined;
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [areTasksVisible, setAreTasksVisible] = useState(false);
  // Drives the blocking loader over the task list while a drag-reorder is being
  // persisted. Tracked locally (not off `updateTaskMutation.isPending`) because
  // a single reorder can fan out into several concurrent PUTs and we want one
  // overlay that stays up until every `challenge_order` write resolves — same
  // pattern as the challenge reorder in `ChallengesAndTasks`.
  const [isPersistingTaskReorder, setIsPersistingTaskReorder] =
    useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  /** Controls the confirm-before-delete popup for the task open in the Edit
   *  Task modal. Mirrors `isDeleteConfirmOpen` (the challenge equivalent) so
   *  the destructive task action goes through the same explicit confirm step
   *  as deleting a challenge / season. */
  const [isTaskDeleteConfirmOpen, setIsTaskDeleteConfirmOpen] =
    useState(false);
  /** When non-null, the task modal is in edit-mode for that task id. When
   *  null, the modal (if open) is in create-mode. Kept separate from
   *  `isTaskModalOpen` so the modal's `initialValues` prop is stable while
   *  the close animation runs. */
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  /** When non-null, the read-only view modal renders for that task id.
   *  Driven by the per-task eye icon in the collapsed task list — reachable
   *  from the wizard collapsed state AND from the Season Details locked
   *  variant, so the same admin can preview a saved task without ever
   *  opening the editor. Independent of `editingTaskId` so the two paths
   *  (eye → view; pencil → edit) never collide. */
  const [viewingTaskId, setViewingTaskId] = useState<string | null>(null);

  const update = useCallback(
    (patch: Partial<ChallengeDraft>) => onChange({ ...challenge, ...patch }),
    [challenge, onChange],
  );

  // ─── Reward editing helpers ────────────────────────────────────────────
  const updateReward = useCallback(
    (id: string, patch: Partial<RewardDraft>) => {
      update({
        rewards: challenge.rewards.map((r) =>
          r.id === id ? { ...r, ...patch } : r,
        ),
      });
    },
    [challenge.rewards, update],
  );

  const handleAddReward = useCallback(() => {
    update({
      rewards: [...challenge.rewards, createEmptyReward(challenge.rewards.length)],
    });
  }, [challenge.rewards, update]);

  const handleRemoveReward = useCallback(
    (id: string) => {
      update({ rewards: challenge.rewards.filter((r) => r.id !== id) });
    },
    [challenge.rewards, update],
  );

  // ─── Task helpers ──────────────────────────────────────────────────────
  const handleTaskSubmit = useCallback(
    async (task: TaskDraft) => {
      // ── Edit mode ─────────────────────────────────────────────────
      // When `seasonId` is present the existing task lives on the BE
      // (its `task.id` is the server-assigned id stamped on create-
      // success, see the create branch below). Fire
      // `PUT /game-pass-admin/v2/admin/tasks/{task_id}` and only commit to
      // local state once the API confirms — same pattern as create.
      // Without a `seasonId` (locked-mode surfaces, isolated unit
      // tests) we keep the original local-only edit so those call
      // sites don't have to mock the mutation.
      if (editingTaskId !== null) {
        let committedTask = task;
        if (seasonId) {
          try {
            // Presign + PUT a newly-picked task image (no-op when none), or
            // resolve to `null` when the admin cleared the persisted image, so
            // the PATCH either sends the new key, clears it, or leaves it be.
            const imgKey = await resolveUpdateImageKey(
              task.imageFile,
              task.imgKey,
              "tasks",
            );
            const body = mapTaskDraftToUpdateTaskRequest(task, imgKey);
            const updatedTask = await updateTaskMutation.mutateAsync({
              taskId: editingTaskId,
              body,
              seasonId,
            });
            // Reconcile the draft with what's now persisted: stamp the
            // resolved key + the BE preview URL and drop the consumed local
            // file so a re-edit shows the saved image instead of a stale or
            // broken preview.
            committedTask = {
              ...task,
              imgKey: commitResolvedImgKey(imgKey, task.imgKey),
              imageUrl: commitResolvedImageUrl(
                updatedTask?.image_url,
                task.imageUrl,
              ),
              imageFile: undefined,
            };
          } catch (error) {
            message.error(
              getApiErrorMessage(error, AppStrings.UPDATE_TASK_FAILED),
            );
            return;
          }
        }
        update({
          tasks: challenge.tasks.map((t) =>
            t.id === editingTaskId ? committedTask : t,
          ),
        });
        setIsTaskModalOpen(false);
        setEditingTaskId(null);
        return;
      }

      // ── Create mode ───────────────────────────────────────────────
      // Without a `seasonId` we still fall back to local-only so
      // locked-mode call sites + isolated unit tests don't have to
      // mock the mutation. (The wizard always supplies a `seasonId`
      // by Step 2, so production never hits this branch.)
      if (!seasonId) {
        update({ tasks: [...challenge.tasks, task] });
        setIsTaskModalOpen(false);
        return;
      }

      let createdTask;
      let resolvedImgKey: string | undefined;
      let createBody;
      try {
        // Presign + PUT the task image to S3 first (no-op when no image is
        // attached) so the `img_key` is ready before the create POST.
        resolvedImgKey = await resolveImageKey(task.imageFile, "tasks");
        createBody = mapTaskDraftToCreateTaskRequest(task, {
          seasonId,
          challengeId: challenge.persistedId ?? null,
          challengeName: challenge.name,
          // `challenge_order` is the task's zero-based slot *within* the
          // challenge — the next free position is the current task count
          // (the new task isn't in `challenge.tasks` yet). This makes tasks
          // default to creation order; drag-reorder later overwrites it.
          challengeOrder: challenge.tasks.length,
          imgKey: resolvedImgKey,
        });
        createdTask = await createTaskMutation.mutateAsync(createBody);
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.CREATE_TASK_FAILED),
        );
        // Modal stays open so the admin can correct + retry without
        // re-typing the entire form.
        return;
      }

      // Replace the locally-generated id with the server-assigned id
      // *before* committing to state. Two things depend on this:
      //   1. The edit pencil's PUT — `editingTaskId` flows straight
      //      into the `/tasks/{task_id}` URL, so it MUST be the
      //      server id from this point on.
      //   2. React/dnd-kit keys — we stamp the id once, here, before
      //      the row is ever mounted, so the row never has to re-key
      //      from a local id to a server id (which would otherwise
      //      unmount/remount and reset focus + transitions).
      // Reconcile the draft with what's now persisted: stamp the server id +
      // uploaded key and drop the consumed local file so a later edit reflects
      // the real state instead of re-uploading or showing a stale preview.
      // `createdTask` can be null when the BE returns a success body without a
      // task payload (FE-1692) — the task is still created, so we keep the
      // locally-generated id rather than treating the response as a failure.
      update({
        tasks: [
          ...challenge.tasks,
          {
            ...task,
            id: createdTask?.id ?? task.id,
            imgKey: commitResolvedImgKey(resolvedImgKey, task.imgKey),
            imageUrl: commitResolvedImageUrl(
              createdTask?.image_url,
              task.imageUrl,
            ),
            imageFile: undefined,
          },
        ],
      });
      message.success(AppStrings.CREATE_TASK_SUCCESS);
      setIsTaskModalOpen(false);
    },
    [
      challenge.name,
      challenge.persistedId,
      challenge.tasks,
      createTaskMutation,
      editingTaskId,
      resolveImageKey,
      resolveUpdateImageKey,
      seasonId,
      update,
      updateTaskMutation,
    ],
  );

  /** Paste the clipboard task into this challenge. Mirrors the create branch of
   *  `handleTaskSubmit` (kept separate so that well-tested submit path stays
   *  untouched): clone the clipboard draft into a fresh, not-yet-persisted task,
   *  then either append it locally (no `seasonId` — locked-mode surfaces /
   *  isolated tests) or upload its image + `POST /tasks` under this challenge and
   *  commit with the server id. The clipboard is intentionally NOT cleared so the
   *  same task can be pasted into multiple challenges. */
  const handlePasteTask = useCallback(async () => {
    if (!copiedTask) return;
    // Belt-and-suspenders: the button is disabled in this state, but guard the
    // handler too so a programmatic / keyboard path can't paste before the
    // challenge has a server id to attach the task to.
    if (Boolean(seasonId) && !challenge.persistedId) return;

    const pasted = duplicateTaskDraft(copiedTask, challenge.tasks.length + 1);

    if (!seasonId) {
      update({ tasks: [...challenge.tasks, pasted] });
      message.success(AppStrings.PASTE_TASK_SUCCESS);
      return;
    }

    let createdTask;
    let resolvedImgKey: string | undefined;
    try {
      // Upload a not-yet-saved image, otherwise re-reference the copied
      // `img_key` so the paste keeps its artwork; a task with no image ships
      // nothing.
      resolvedImgKey =
        (await resolveImageKey(pasted.imageFile, "tasks")) ??
        pasted.imgKey ??
        undefined;
      createdTask = await createTaskMutation.mutateAsync(
        mapTaskDraftToCreateTaskRequest(
          stampTaskSubmissionModeAtSave(pasted, defaultSubmissionModes),
          {
            seasonId,
            challengeId: challenge.persistedId ?? null,
            challengeName: challenge.name,
            // Append at the next free slot — same ordering contract as create.
            challengeOrder: challenge.tasks.length,
            imgKey: resolvedImgKey,
          },
        ),
      );
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.CREATE_TASK_FAILED),
      );
      return;
    }

    update({
      tasks: [
        ...challenge.tasks,
        {
          ...stampTaskSubmissionModeAtSave(pasted, defaultSubmissionModes),
          id: createdTask?.id ?? pasted.id,
          imgKey: commitResolvedImgKey(resolvedImgKey, pasted.imgKey),
          imageUrl: commitResolvedImageUrl(
            createdTask?.image_url,
            pasted.imageUrl,
          ),
          imageFile: undefined,
        },
      ],
    });
    message.success(AppStrings.PASTE_TASK_SUCCESS);
  }, [
    challenge.name,
    challenge.persistedId,
    challenge.tasks,
    copiedTask,
    createTaskMutation,
    defaultSubmissionModes,
    resolveImageKey,
    seasonId,
    update,
  ]);

  /** Persist every locally-held task of a freshly-created challenge and return
   *  the reconciled list. A duplicated challenge arrives with the source's
   *  tasks copied as local drafts; the "Add Task" CTA is disabled until a
   *  challenge has a server id, so a not-yet-saved challenge can only ever hold
   *  these local copies (never server-side tasks). Once the parent challenge is
   *  persisted we POST each task — uploading a not-yet-saved image or
   *  re-referencing the copied `img_key` — and stamp the returned id back so a
   *  later edit / delete targets the right `/tasks/{task_id}`. Failures are
   *  isolated per task: successful copies still commit, failed rows stay local
   *  for a retry, and `failureCount` drives the caller's collapse decision. */
  const persistChallengeTasks = useCallback(
    async (
      challengeId: string,
    ): Promise<{ tasks: ReadonlyArray<TaskDraft>; failureCount: number }> => {
      if (!seasonId || challenge.tasks.length === 0) {
        return { tasks: challenge.tasks, failureCount: 0 };
      }
      const nextTasks: TaskDraft[] = [];
      let failureCount = 0;
      // Sequential (not parallel) so each task's `challenge_order` matches its
      // slot and a mid-list failure can't reshuffle the surviving order.
      for (let order = 0; order < challenge.tasks.length; order += 1) {
        const task = challenge.tasks[order];
        const taskAtSave = stampTaskSubmissionModeAtSave(
          task,
          defaultSubmissionModes,
        );
        try {
          // Upload a not-yet-saved image, otherwise re-reference the copied
          // `img_key` so the duplicate keeps its artwork; a task with no image
          // ships nothing.
          const imgKey =
            (await resolveImageKey(task.imageFile, "tasks")) ??
            task.imgKey ??
            undefined;
          const createdTask = await createTaskMutation.mutateAsync(
            mapTaskDraftToCreateTaskRequest(taskAtSave, {
              seasonId,
              challengeId,
              challengeName: challenge.name,
              challengeOrder: order,
              imgKey,
            }),
          );
          nextTasks.push({
            ...taskAtSave,
            id: createdTask?.id ?? task.id,
            challengeOrder: order,
            imgKey,
            imageUrl: commitResolvedImageUrl(
              createdTask?.image_url,
              task.imageUrl,
            ),
            imageFile: undefined,
          });
        } catch (error) {
          failureCount += 1;
          message.error(
            getApiErrorMessage(error, AppStrings.CREATE_TASK_FAILED),
          );
          // Keep the row as-is so the admin can retry it instead of losing it.
          nextTasks.push(task);
        }
      }
      return { tasks: nextTasks, failureCount };
    },
    [
      challenge.name,
      challenge.tasks,
      createTaskMutation,
      defaultSubmissionModes,
      resolveImageKey,
      seasonId,
    ],
  );

  /** Persists the challenge's reward rows via the dedicated reward PUT.
   *  Failures are isolated from the metadata / create commit so a retry
   *  cannot orphan or duplicate an already-saved challenge. */
  const persistChallengeRewards = useCallback(
    async (
      challengeId: string,
      failureFallback: string,
    ): Promise<boolean> => {
      try {
        await updateChallengeRewardMutation.mutateAsync({
          challengeId,
          seasonId,
          body: mapChallengeDraftToUpdateChallengeRewardRequest(
            challenge.rewards,
            challenge.pathAvailability,
          ),
        });
        return true;
      } catch (error) {
        message.error(getApiErrorMessage(error, failureFallback));
        return false;
      }
    },
    [
      challenge.pathAvailability,
      challenge.rewards,
      seasonId,
      updateChallengeRewardMutation,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!seasonId) {
      onSave();
      return;
    }

    if (challenge.persistedId) {
      try {
        // Presign + PUT a newly-picked icon (no-op when none), or resolve to
        // `null` when the admin cleared the persisted icon, so the PATCH either
        // sends the new key, clears it, or leaves it untouched.
        const imgKey = await resolveUpdateImageKey(
          challenge.iconFile,
          challenge.imgKey,
          "challenges",
        );
        const updatedChallenge = await updateChallengeMutation.mutateAsync({
          challengeId: challenge.persistedId,
          seasonId,
          body: mapChallengeDraftToUpdateChallengeRequest(
            challenge,
            index - 1,
            imgKey,
          ),
        });
        // Metadata is committed before the reward PUT so a reward failure
        // cannot leave the card in an unsaved state and invite a duplicate
        // metadata retry.
        onChange({
          ...challenge,
          isSaved: true,
          isExpanded: false,
          imgKey: commitResolvedImgKey(imgKey, challenge.imgKey),
          imageUrl: commitResolvedImageUrl(
            updatedChallenge?.image_url,
            challenge.imageUrl,
          ),
          iconFile: undefined,
        });
        await persistChallengeRewards(
          challenge.persistedId,
          "Metadata saved — rewards failed to update",
        );
      } catch (error) {
        message.error(
          getApiErrorMessage(error, "Failed to update challenge"),
        );
      }
      return;
    }

    if (challenge.isSaved) {
      onSave();
      return;
    }

    try {
      // Presign + PUT a newly-picked challenge icon (no-op when none); fall
      // back to the draft's existing `img_key` so a duplicated challenge keeps
      // its copied icon, and send nothing when the challenge has no image.
      const imgKey =
        (await resolveImageKey(challenge.iconFile, "challenges")) ??
        challenge.imgKey ??
        undefined;
      const createdChallenge = await createChallengeMutation.mutateAsync({
        seasonId,
        sortOrder: index - 1,
        draft: challenge,
        imgKey,
      } satisfies CreateChallengeVariables);
      // The challenge is created by this point. A missing id (null body or an
      // unrecognised envelope — FE-1692) must NOT read as a failure: we commit
      // the challenge as saved without a `persistedId`. The "Add Task" CTA
      // stays disabled until the id is known (it gates on `persistedId`), but
      // the admin no longer sees a misleading error after a successful save.
      const persistedId =
        createdChallenge?.id ?? createdChallenge?.challenge_id ?? undefined;
      // Now that the parent has a server id, persist any copied tasks against
      // it so a duplicated challenge is saved end-to-end (challenge + each
      // task + their images). A net-new challenge has no tasks yet, so this is
      // a no-op for the normal flow. Task failures are isolated inside the
      // helper and must NOT abort the challenge commit below — the challenge
      // already exists on the BE, so dropping its `persistedId` here would
      // orphan it and let a retry create a duplicate.
      const { tasks: persistedTasks, failureCount } = persistedId
        ? await persistChallengeTasks(persistedId)
        : { tasks: challenge.tasks, failureCount: 0 };
      // Commit the challenge before the reward PUT so a reward failure cannot
      // leave the card unsaved and invite a duplicate create on retry.
      onChange({
        ...challenge,
        persistedId,
        tasks: persistedTasks,
        isSaved: true,
        // Collapse to the summary on a clean save; keep the editor open when a
        // copied task failed to persist so the admin can see the still-local
        // rows and retry them.
        isExpanded: failureCount > 0,
        imgKey: commitResolvedImgKey(imgKey, challenge.imgKey),
        imageUrl: commitResolvedImageUrl(
          createdChallenge?.image_url,
          challenge.imageUrl,
        ),
        iconFile: undefined,
      });
      message.success(AppStrings.CREATE_CHALLENGE_SUCCESS);
      if (persistedId) {
        await persistChallengeRewards(
          persistedId,
          "Challenge created — rewards failed to save",
        );
      }
    } catch (error) {
      message.error(
        getApiErrorMessage(error, "Failed to create challenge"),
      );
    }
  }, [
    challenge,
    createChallengeMutation,
    index,
    onChange,
    onSave,
    persistChallengeRewards,
    persistChallengeTasks,
    resolveImageKey,
    resolveUpdateImageKey,
    seasonId,
    updateChallengeMutation,
    updateChallengeRewardMutation,
  ]);

  const handleOpenCreateTask = useCallback(() => {
    // Belt-and-suspenders: the button is rendered `disabled` in this
    // state, but guard the handler too so a programmatic / keyboard
    // edge case can't open the create modal before the challenge has a
    // server id to attach the task to.
    if (Boolean(seasonId) && !challenge.persistedId) return;
    setEditingTaskId(null);
    setIsTaskModalOpen(true);
  }, [challenge.persistedId, seasonId]);

  const handleOpenEditTask = useCallback((id: string) => {
    setEditingTaskId(id);
    setIsTaskModalOpen(true);
  }, []);

  const handleCloseTaskModal = useCallback(() => {
    setIsTaskModalOpen(false);
    setEditingTaskId(null);
  }, []);

  /** Opens the confirm-before-delete popup from the Edit Task modal's Delete
   *  button. The actual delete only runs after the admin confirms — matching
   *  the challenge / season destructive flow. */
  const handleRequestDeleteTask = useCallback(() => {
    setIsTaskDeleteConfirmOpen(true);
  }, []);

  const handleCancelDeleteTask = useCallback(() => {
    setIsTaskDeleteConfirmOpen(false);
  }, []);

  /** Called after the admin confirms deletion in `ConfirmDeleteTaskModal`.
   *
   *  When `seasonId` is present the task lives on the BE (its `task.id` is the
   *  server-assigned id stamped on create-success), so we fire
   *  `DELETE /game-pass-admin/v2/admin/tasks/{task_id}` and only drop it from
   *  local state once the API confirms — same persist-then-commit pattern as
   *  `handleConfirmDelete` for challenges. On failure we surface the API error
   *  and leave the Edit Task modal open so the admin can retry; the confirm
   *  popup closes either way to match the existing destructive-flow UX.
   *  Without a `seasonId` (locked-mode surfaces, isolated unit tests) the task
   *  was never persisted, so we keep the original local-only removal. */
  const handleConfirmDeleteTask = useCallback(async () => {
    if (editingTaskId === null) {
      setIsTaskDeleteConfirmOpen(false);
      return;
    }
    if (seasonId) {
      try {
        await deleteTaskMutation.mutateAsync({
          taskId: editingTaskId,
          seasonId,
        });
      } catch (error) {
        message.error(
          getApiErrorMessage(error, AppStrings.DELETE_TASK_FAILED),
        );
        // Close the confirm popup but keep the Edit Task modal open so the
        // admin can retry without re-finding the task.
        setIsTaskDeleteConfirmOpen(false);
        return;
      }
    }
    update({ tasks: challenge.tasks.filter((t) => t.id !== editingTaskId) });
    setIsTaskDeleteConfirmOpen(false);
    setIsTaskModalOpen(false);
    setEditingTaskId(null);
  }, [challenge.tasks, deleteTaskMutation, editingTaskId, seasonId, update]);

  // The next task's index inside this challenge — fed into the modal so the
  // generated id stays unique even when several tasks land in the same ms.
  const nextTaskIndex = useMemo(
    () => challenge.tasks.length + 1,
    [challenge.tasks.length],
  );

  // Task creation hits `POST /game-pass-admin/v2/admin/tasks` with the parent
  // `challenge_id`, which only exists once the challenge has been saved to
  // the BE (its `persistedId` is stamped on create-success in `handleSave`).
  // So the "Add Task" CTA stays disabled until then. Only enforced on the
  // API-wired path (`seasonId` present); local-only call sites (locked-mode
  // SeasonDetails before the detail endpoint is wired, isolated unit tests)
  // keep the original always-enabled behaviour.
  const isAddTaskDisabled = Boolean(seasonId) && !challenge.persistedId;

  // The per-task Copy icon shows on every editable card (a locked / Active
  // season can't paste within the same season, which is the only paste scope
  // today). Resolved once so both task-list surfaces (collapsed + expanded)
  // share the same gate.
  const taskCopyHandler = !isLocked ? onCopyTask : undefined;

  // The "Paste Task" button renders next to "Add Task" only when the clipboard
  // holds a task. It shares "Add Task"'s save gating (a task can't be created
  // until the parent challenge has a server id).
  const canPasteTask = copiedTask !== null;

  // ─── Task drag-and-drop wiring ─────────────────────────────────────────
  // Each challenge card owns its own sortable scope for its task list. We
  // intentionally do NOT lift this into `ChallengesAndTasks` so:
  //  - the parent's DndContext stays focused on reordering challenges, and
  //  - a card that's mounted standalone (e.g. inside EditSeasonModal in a
  //    future surface) still gets working task drag without the parent
  //    having to set up another context.
  //
  // `PointerSensor` uses a 4px activation distance so the eye/edit icon
  // clicks aren't swallowed by an accidental micro-drag. `KeyboardSensor`
  // pairs with `sortableKeyboardCoordinates` to give the drag handle full
  // keyboard support (Space to pick up, Arrows to move, Space to drop).
  const taskSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // dnd-kit's SortableContext expects a stable items array of ids. Memo'd
  // against `challenge.tasks` so React identity is preserved across
  // unrelated re-renders (description edits, reward chip toggles, etc.).
  const taskIds = useMemo(
    () => challenge.tasks.map((task) => task.id),
    [challenge.tasks],
  );

  const handleTaskDragEnd = useCallback(
    async (event: DragEndEvent) => {
      // Ignore drops that land while a previous reorder is still saving — the
      // overlay blocks pointer drags, but a queued keyboard drop could
      // otherwise race the in-flight PUTs.
      if (isPersistingTaskReorder) return;
      const { active, over } = event;
      // `over` is null when the user drops outside any sortable item — we
      // treat it as a cancel rather than a reorder. `reorderTasks` further
      // returns the original array reference when the move is a no-op
      // (same id, unknown id), so the parent only sees a real change.
      if (!over || active.id === over.id) return;
      const previousTasks = challenge.tasks;
      const nextTasks = reorderTasks(
        previousTasks,
        String(active.id),
        String(over.id),
      );
      if (nextTasks === previousTasks) return;

      // Optimistically apply the new order so the list reflows immediately.
      update({ tasks: nextTasks });

      // Local-only call sites (locked-mode surfaces, isolated unit tests) have
      // no backend to persist to — the reorder lives in local state only,
      // mirroring the create / update / delete fallbacks above.
      if (!seasonId) return;

      // Persist the new zero-based `challenge_order` for every task whose
      // position actually changed. The previous position is the task's index in
      // the pre-reorder array (already sorted by `challenge_order` on load — see
      // `sortTasksByChallengeOrder`), so comparing array indices is sufficient:
      // unchanged tasks keep their stored index and are skipped. Tasks that
      // never received a server id (FE-1692: a create succeeded but returned no
      // id) keep a locally-generated `task-…` id and cannot be PUT, so they're
      // filtered out.
      const previousIndexById = new Map(
        previousTasks.map((task, index) => [task.id, index]),
      );
      const attempted = nextTasks
        .map((task, index) => ({ id: task.id, index }))
        .filter(
          ({ id, index }) =>
            !String(id).startsWith("task-") &&
            previousIndexById.get(id) !== index,
        );

      if (attempted.length === 0) return;

      setIsPersistingTaskReorder(true);
      // Persist each changed task's `challenge_order` independently. `allSettled`
      // (not `all`) so a single failed PUT doesn't discard the writes that DID
      // land — we reconcile the list against exactly which ids persisted, so a
      // partial failure leaves the frontend order consistent with the backend
      // instead of snapping fully back over already-committed writes (FE-1727).
      const results = await Promise.allSettled(
        attempted.map(({ id, index }) =>
          updateTaskMutation.mutateAsync({
            taskId: id,
            seasonId,
            body: { challenge_order: index },
          }),
        ),
      );
      const failedIds = new Set(
        attempted
          .filter((_, i) => results[i].status === "rejected")
          .map(({ id }) => id),
      );
      // Re-derive the order from what actually persisted (successful ids keep
      // their new index; failed / unchanged ids keep the index the backend
      // still holds), matching what the next reload would show.
      update({
        tasks: reconcileTaskReorder(previousTasks, attempted, failedIds),
      });
      if (failedIds.size === 0) {
        message.success(AppStrings.TASK_REORDER_SUCCESS);
      } else {
        const firstRejection = results.find(
          (result): result is PromiseRejectedResult =>
            result.status === "rejected",
        );
        message.error(
          getApiErrorMessage(
            firstRejection?.reason,
            AppStrings.TASK_REORDER_FAILED,
          ),
        );
      }
      setIsPersistingTaskReorder(false);
    },
    [
      challenge.tasks,
      isPersistingTaskReorder,
      seasonId,
      update,
      updateTaskMutation,
    ],
  );

  /** The full task object being edited, looked up live from the parent's
   *  source of truth so any in-flight changes survive a re-render. */
  const editingTask = useMemo(
    () =>
      editingTaskId === null
        ? undefined
        : challenge.tasks.find((t) => t.id === editingTaskId),
    [challenge.tasks, editingTaskId],
  );

  /** The task currently being previewed in view mode (eye-icon path). */
  const viewingTask = useMemo(
    () =>
      viewingTaskId === null
        ? undefined
        : challenge.tasks.find((t) => t.id === viewingTaskId),
    [challenge.tasks, viewingTaskId],
  );

  const handleOpenViewTask = useCallback((id: string) => {
    setViewingTaskId(id);
  }, []);

  const handleCloseViewTask = useCallback(() => {
    setViewingTaskId(null);
  }, []);

  // Per the Figma destructive flow, the Delete button opens a confirmation
  // modal first. The actual `onDelete` is only invoked after explicit confirm.
  const handleRequestDelete = useCallback(() => {
    setIsDeleteConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setIsDeleteConfirmOpen(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    // A challenge that was never persisted (no server id, or the
    // pre-save local-only flow with no seasonId) is dropped locally —
    // there's nothing on the backend to delete. Mirrors the create /
    // update split in `handleSave`.
    if (seasonId && challenge.persistedId) {
      try {
        await deleteChallengeMutation.mutateAsync({
          challengeId: challenge.persistedId,
          seasonId,
        });
      } catch (error) {
        // Keep the card in place so the admin can retry; the modal
        // closes either way to match the existing destructive-flow UX.
        message.error(getApiErrorMessage(error, "Failed to delete challenge"));
        setIsDeleteConfirmOpen(false);
        return;
      }
    }
    setIsDeleteConfirmOpen(false);
    onDelete();
  }, [challenge.persistedId, deleteChallengeMutation, onDelete, seasonId]);

  // Challenge reward dropdown — driven by `config.reward_types` (the distinct
  // reward currencies). Falls back to the full static reward catalogue while
  // the config loads.
  const rewardTypeOptions = useMemo(
    () => getRewardTypeOptions(gamePassConfig),
    [gamePassConfig],
  );

  // Lookups so a reward row can resolve its selected option (for bonus params /
  // amount label) and the collapsed chips can resolve config-driven labels.
  const rewardOptionByValue = useMemo(
    () => new Map(rewardTypeOptions.map((option) => [option.value, option])),
    [rewardTypeOptions],
  );
  const rewardLabelByValue = useMemo(
    () => new Map(rewardTypeOptions.map((option) => [option.value, option.label])),
    [rewardTypeOptions],
  );

  // One reward row per distinct reward type — derived from the active option
  // count (currency + bonus rewards when API-driven, the static set otherwise)
  // so the cap can never drift from what the picker actually offers.
  const maxRewards = rewardTypeOptions.length;

  // Picking a reward type stamps the row with everything the wire needs: a
  // bonus option carries its grant id + currency and seeds its params with
  // their defaults (optional params prefilled, required left blank); a currency
  // option clears any leftover bonus fields so the row round-trips as a plain
  // play/value reward.
  const handleRewardTypeChange = useCallback(
    (id: string, value: string) => {
      const option = rewardOptionByValue.get(value);
      if (option?.kind === "bonus") {
        updateReward(id, {
          type: value,
          bonusRewardType: option.bonusRewardType,
          bonusCurrency: option.bonusCurrency,
          params: buildDefaultRewardParams(option.params),
        });
        return;
      }
      updateReward(id, {
        type: value,
        bonusRewardType: undefined,
        bonusCurrency: undefined,
        params: undefined,
      });
    },
    [rewardOptionByValue, updateReward],
  );

  const updateRewardParam = useCallback(
    (id: string, paramName: string, value: string) => {
      const current = challenge.rewards.find((r) => r.id === id);
      updateReward(id, {
        params: { ...(current?.params ?? {}), [paramName]: value },
      });
    },
    [challenge.rewards, updateReward],
  );

  // Required bonus params that are still incomplete, mapped to their labels. A
  // bonus reward isn't save-ready until every `required` param holds a
  // well-formed value for its type — a blank, or a partial signed entry like
  // "-" that would ship as a non-numeric string, keeps the requirement open.
  // These feed both the Save gate and the inline "what's missing" hint
  // (mirroring the name / reward requirement treatment).
  const missingRewardParamLabels = useMemo(() => {
    const labels: string[] = [];
    for (const reward of challenge.rewards) {
      const option = reward.type
        ? rewardOptionByValue.get(reward.type)
        : undefined;
      if (option?.kind !== "bonus") continue;
      for (const param of option.params ?? []) {
        if (!param.required) continue;
        if (!isParamValueComplete(param.type, reward.params?.[param.name])) {
          labels.push(humanizeParamName(param.name));
        }
      }
    }
    return labels;
  }, [challenge.rewards, rewardOptionByValue]);

  // Task modal option catalogues, derived from `GET /config` + `GET
  // /task-options` and forwarded to every `CreateTaskModal` this card mounts.
  const taskTypeOptions = useMemo(
    () => getTaskTypeOptions(taskOptions),
    [taskOptions],
  );
  const betTypeOptions = useMemo(
    () => getBetTypeOptions(taskOptions),
    [taskOptions],
  );
  const qualifierOptions = useMemo(
    () => getQualifierOptions(taskOptions),
    [taskOptions],
  );
  const betCurrencyOptions = useMemo(
    () => getBetCurrencyOptions(gamePassConfig),
    [gamePassConfig],
  );
  const betCurrencyMinimums = useMemo(
    () => getBetCurrencyMinimums(gamePassConfig),
    [gamePassConfig],
  );
  const socialTaskTypeOptions = useMemo(
    () => getSocialTaskTypeOptions(taskOptions),
    [taskOptions],
  );
  const sportOptions = useMemo(() => getSportOptions(taskOptions), [taskOptions]);

  /** Shared option bundle forwarded to every `CreateTaskModal` (create / edit /
   *  view) this card renders, so the dropdowns reflect the live catalogue. The
   *  casino provider/game picker self-fetches its catalogue from the
   *  `casino-games` endpoints, so it isn't part of this bundle. */
  const taskModalOptionProps = {
    taskTypeOptions,
    betTypeOptions,
    qualifierOptions,
    betCurrencyOptions,
    betCurrencyMinimums,
    socialTaskTypeOptions,
    sportOptions,
  };

  // A reward type may only be used once per challenge (one row per distinct
  // type). Collect the types already taken so each row's dropdown can disable
  // the ones picked in OTHER rows — its own current value stays selectable.
  const selectedRewardTypes = useMemo(
    () =>
      new Set(
        challenge.rewards
          .map((reward) => reward.type)
          .filter((type): type is string => Boolean(type)),
      ),
    [challenge.rewards],
  );

  // Required-but-incomplete pieces (name / reward), mapped to their labels.
  // Rendered as an inline hint next to a disabled Save button so the admin
  // knows what's missing — important when an older challenge loads with an
  // incomplete reward row rather than just showing a greyed-out button.
  const missingRequirementLabels = useMemo(
    () => [
      ...getMissingChallengeRequirements(challenge).map(
        (key) => CHALLENGE_REQUIREMENT_LABELS[key],
      ),
      ...missingRewardParamLabels,
    ],
    [challenge, missingRewardParamLabels],
  );

  // Blocking loader shown over a task list while a drag-reorder is persisted.
  // Captures pointer events (via the overlay's absolute fill) so the admin
  // can't start another drag until every `challenge_order` PUT resolves. Shared
  // between the collapsed and expanded task-list branches so both surfaces show
  // identical feedback.
  const taskReorderOverlay = isPersistingTaskReorder ? (
    <div
      className={styles.taskReorderOverlay}
      data-testid="challenge-card-task-reorder-loader"
      role="status"
      aria-live="polite"
      aria-label={AppStrings.TASK_REORDER_SAVING}
    >
      <RebetLoader height={32} width={32} />
    </div>
  ) : null;

  // The view-only modal is opened from the eye icon in the collapsed task
  // list (both the wizard's saved-challenge view AND the Season Details
  // locked view). Lifted to a shared variable so each render branch can
  // include it as a sibling — keeps the modal mounted irrespective of
  // whether we returned through the collapsed or expanded path.
  const viewTaskModal = (
    <CreateTaskModal
      open={viewingTask !== undefined}
      taskIndex={1}
      mode="view"
      initialValues={viewingTask}
      defaultSubmissionModes={defaultSubmissionModes}
      {...taskModalOptionProps}
      onClose={handleCloseViewTask}
      // `onSubmit` is required by the modal's contract but the view-only
      // mode hides the submit button, so this is unreachable. Keeping the
      // no-op explicit (rather than `undefined`) silences the optional
      // chain at the call site.
      onSubmit={handleCloseViewTask}
    />
  );

  // ─── Collapsed summary state ────────────────────────────────────────────
  // Locked mode forces the collapsed branch regardless of `isExpanded`, since
  // the inline editor (Save / Close / Delete / Add Task) has no meaning for
  // an Active season the player is already progressing through.
  if (!challenge.isExpanded || isLocked) {
    return (
      <>
      <article
        ref={sortable?.setNodeRef}
        className={`${styles.card} ${styles.cardCollapsed} ${
          sortable?.isDragging ? styles.cardDragging : ""
        }`.trim()}
        style={sortable?.transformStyle}
        // dnd-kit attaches role / aria-roledescription / tabIndex via
        // `attributes` — spread on the same element that carries the
        // sortable ref so screen readers announce the sortable item.
        {...(sortable?.attributes ?? {})}
        data-testid="challenge-card-collapsed"
      >
        <header className={styles.collapsedHeader}>
          <div className={styles.collapsedTitleGroup}>
            <span className={styles.indexBadge}>{`#${index}`}</span>
            {(challenge.iconFile ||
              challenge.imgKey ||
              challenge.imageUrl) && (
              <span className={styles.iconThumbWrapper}>
                <ChallengeIconThumb
                  file={challenge.iconFile}
                  imgKey={challenge.imgKey}
                  imageUrl={challenge.imageUrl}
                />
              </span>
            )}
            <span className={styles.collapsedName}>
              {challenge.name || `${AppStrings.CHALLENGE_LABEL_PREFIX} ${index}`}
            </span>
          </div>
          {isLocked ? (
            <StatusBadge
              tone="neutral"
              variant="soft"
              size="sm"
              data-testid="challenge-card-locked-badge"
            >
              {AppStrings.SEASON_CARD_LOCKED}
            </StatusBadge>
          ) : (
            <div className={styles.headerActions}>
              <CardIconButton
                ariaLabel={AppStrings.CHALLENGE_DUPLICATE}
                onClick={onDuplicate}
                testId="challenge-card-duplicate"
              >
                <CopyOutlined />
              </CardIconButton>
              {/* Drag handle is rendered as a native <button> (not
                  CardIconButton) so we can forward dnd-kit's activator
                  ref + listeners. When `sortable` is undefined (older
                  call sites, or future locked-mode misuse), it stays
                  disabled and behaves like the original stub. */}
              <button
                type="button"
                ref={sortable?.setActivatorNodeRef}
                className={`${styles.iconButton} ${
                  sortable ? styles.dragHandleActive : ""
                }`.trim()}
                aria-label={AppStrings.CHALLENGE_DRAG_HANDLE}
                data-testid="challenge-card-drag"
                disabled={!sortable}
                {...(sortable?.listeners ?? {})}
              >
                <HolderOutlined />
              </button>
              <CardIconButton
                ariaLabel={AppStrings.CHALLENGE_EDIT}
                onClick={onEdit}
                testId="challenge-card-edit"
              >
                <EditOutlined />
              </CardIconButton>
            </div>
          )}
        </header>

        {challenge.description && (
          <p className={styles.collapsedDescription}>
            {challenge.description}
          </p>
        )}

        {challenge.rewards.some((r) => r.amount || r.type) && (
          <div
            className={styles.collapsedRewardSection}
            data-testid="challenge-card-rewards-section"
          >
            <span className={styles.sectionLabel}>
              {AppStrings.CHALLENGE_REWARDS_LABEL}
            </span>
            <div
              className={styles.rewardChipRow}
              data-testid="challenge-card-reward-chips"
            >
              {challenge.rewards
                .filter((r) => r.amount || r.type)
                .map((reward) => (
                  <StatusBadge
                    key={reward.id}
                    tone="warning"
                    variant="soft"
                    size="sm"
                  >
                    {formatRewardChip(reward, rewardLabelByValue)}
                  </StatusBadge>
                ))}
            </div>
          </div>
        )}

        <button
          type="button"
          className={styles.tasksRow}
          onClick={() => setAreTasksVisible((v) => !v)}
          aria-expanded={areTasksVisible}
          data-testid="challenge-card-tasks-toggle"
        >
          <span className={styles.tasksRowLabel}>
            <span className={styles.tasksRowLabelText}>
              {AppStrings.TASKS_COUNT}
            </span>
            <span className={styles.tasksRowCount}>
              {challenge.tasks.length}
            </span>
          </span>
          {areTasksVisible ? (
            <DownOutlined className={styles.tasksRowIcon} aria-hidden="true" />
          ) : (
            <RightOutlined className={styles.tasksRowIcon} aria-hidden="true" />
          )}
        </button>
        {areTasksVisible && challenge.tasks.length > 0 && (
          // Locked-mode cards in Season Details render outside any DnD
          // scope — the rows are inert by design. Editable cards mount
          // their own DndContext + SortableContext so users can reorder
          // tasks via the per-row drag handle (or keyboard).
          isLocked ? (
            <ul
              className={styles.collapsedTaskList}
              data-testid="challenge-card-task-list"
            >
              {challenge.tasks.map((task) => (
                <CollapsedTaskRow
                  key={task.id}
                  task={task}
                  onView={handleOpenViewTask}
                />
              ))}
            </ul>
          ) : (
            <div className={styles.taskListWrap}>
              <DndContext
                sensors={taskSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTaskDragEnd}
              >
                <SortableContext
                  items={taskIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul
                    className={`${styles.collapsedTaskList} ${
                      isPersistingTaskReorder ? styles.taskListReordering : ""
                    }`.trim()}
                    data-testid="challenge-card-task-list"
                  >
                    {challenge.tasks.map((task) => (
                      <SortableCollapsedTaskRow
                        key={task.id}
                        task={task}
                        onView={handleOpenViewTask}
                        onCopy={taskCopyHandler}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
              {taskReorderOverlay}
            </div>
          )
        )}
      </article>
      {viewTaskModal}
      </>
    );
  }

  // ─── Editing state ─────────────────────────────────────────────────────
  return (
    <>
      <article
        className={`${styles.card} ${styles.cardExpanded}`}
        data-testid="challenge-card-expanded"
      >
        <header className={styles.expandedHeader}>
          <span className={styles.expandedTitle}>
            {challenge.name || `${AppStrings.CHALLENGE_LABEL_PREFIX} ${index}`}
          </span>
          <div className={styles.headerActions}>
            <CardIconButton
              ariaLabel={AppStrings.CHALLENGE_DUPLICATE}
              onClick={onDuplicate}
              testId="challenge-card-duplicate"
            >
              <CopyOutlined />
            </CardIconButton>
            <CardIconButton
              ariaLabel={AppStrings.CHALLENGE_DRAG_HANDLE}
              onClick={() => undefined}
              testId="challenge-card-drag"
              disabled
            >
              <HolderOutlined />
            </CardIconButton>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              data-testid="challenge-card-close"
            >
              {AppStrings.CHALLENGE_CLOSE}
            </button>
          </div>
        </header>

        <div className={styles.formGrid}>
          <div className={styles.iconColumn}>
            <span className={styles.fieldLabel}>
              {AppStrings.CHALLENGE_ICON}
            </span>
            <FileDropzone
              value={challenge.iconFile ?? null}
              onChange={(file) =>
                update({ iconFile: file ?? undefined })
              }
              accept="image/*"
              initialUrl={
                challenge.imageUrl ?? buildGamePassImageUrl(challenge.imgKey)
              }
              onRemoveInitial={() => update({ imgKey: null, imageUrl: null })}
              hint={AppStrings.CHALLENGE_ICON_HINT}
              buttonLabel={AppStrings.CHALLENGE_ICON_UPLOAD}
              className={styles.iconDropzone}
              data-testid="challenge-card-icon-dropzone"
            />
          </div>

          <div className={styles.fieldsColumn}>
            <label className={styles.field}>
              <span className={styles.srOnly}>
                {AppStrings.CHALLENGE_NAME}
              </span>
              <Input
                className={styles.input}
                value={challenge.name}
                placeholder={AppStrings.CHALLENGE_NAME_PLACEHOLDER}
                onChange={(e) => update({ name: e.target.value })}
                data-testid="challenge-card-name"
              />
            </label>

            <label className={styles.field}>
              <span className={styles.srOnly}>
                {AppStrings.CHALLENGE_DESCRIPTION}
              </span>
              <div className={styles.textareaWrapper}>
                <TextArea
                  className={`custom-form-input ${styles.textarea}`}
                  value={challenge.description}
                  placeholder={AppStrings.CHALLENGE_DESCRIPTION_PLACEHOLDER}
                  autoSize={{ minRows: 3, maxRows: 5 }}
                  maxLength={DESCRIPTION_MAX}
                  onChange={(e) => update({ description: e.target.value })}
                  data-testid="challenge-card-description"
                />
                <span
                  className={styles.charCount}
                  aria-label={AppStrings.CREATE_TASK_DESCRIPTION_COUNTER_ARIA}
                  data-testid="challenge-card-description-count"
                >
                  {`${challenge.description.length}/${DESCRIPTION_MAX}`}
                </span>
              </div>
            </label>

            <div className={styles.rewardSection}>
              <span className={styles.sectionLabel}>
                {AppStrings.REWARD}
              </span>
              <ul
                className={styles.rewardList}
                data-testid="challenge-card-reward-list"
              >
                {challenge.rewards.map((reward) => {
                  const selectedOption = reward.type
                    ? rewardOptionByValue.get(reward.type)
                    : undefined;
                  const paramDefs =
                    selectedOption?.kind === "bonus"
                      ? selectedOption.params ?? []
                      : [];
                  return (
                    <li key={reward.id} className={styles.rewardEntry}>
                      <div className={styles.rewardRow}>
                        <Input
                          className={styles.rewardAmount}
                          value={reward.amount}
                          inputMode="decimal"
                          placeholder={
                            selectedOption?.amountLabel ??
                            AppStrings.REWARD_AMOUNT_PLACEHOLDER
                          }
                          onChange={(e) =>
                            updateReward(reward.id, {
                              amount: sanitizeDecimalInput(e.target.value),
                            })
                          }
                          data-testid={`challenge-card-reward-amount-${reward.id}`}
                        />
                        <Select<string>
                          className={styles.rewardType}
                          value={reward.type}
                          placeholder={AppStrings.REWARD_TYPE_PLACEHOLDER}
                          options={rewardTypeOptions.map((option) => ({
                            value: option.value,
                            label: option.label,
                            disabled:
                              option.value !== reward.type &&
                              selectedRewardTypes.has(option.value),
                          }))}
                          onChange={(value) =>
                            handleRewardTypeChange(reward.id, value)
                          }
                          data-testid={`challenge-card-reward-type-${reward.id}`}
                        />
                        {challenge.rewards.length > 1 && (
                          <button
                            type="button"
                            className={styles.linkButton}
                            onClick={() => handleRemoveReward(reward.id)}
                            aria-label={AppStrings.REMOVE_REWARD}
                            data-testid={`challenge-card-reward-remove-${reward.id}`}
                          >
                            ×
                          </button>
                        )}
                      </div>
                      {paramDefs.length > 0 && (
                        <div
                          className={styles.rewardParams}
                          data-testid={`challenge-card-reward-params-${reward.id}`}
                        >
                          {paramDefs.map((param) => (
                            <label
                              key={param.name}
                              className={styles.rewardParamField}
                            >
                              <span className={styles.rewardParamLabel}>
                                <span className={styles.rewardParamLabelText}>
                                  {humanizeParamName(param.name)}
                                  {param.required ? (
                                    <span
                                      className={styles.rewardParamRequired}
                                      aria-hidden="true"
                                    >
                                      {" *"}
                                    </span>
                                  ) : (
                                    ` (${AppStrings.CHALLENGE_REWARD_PARAM_OPTIONAL})`
                                  )}
                                </span>
                                {param.description && (
                                  <Tooltip title={param.description}>
                                    <InfoCircleOutlined
                                      className={styles.rewardParamInfoIcon}
                                      role="img"
                                      aria-label={param.description}
                                      data-testid={`challenge-card-reward-param-info-${reward.id}-${param.name}`}
                                    />
                                  </Tooltip>
                                )}
                              </span>
                              <Input
                                value={reward.params?.[param.name] ?? ""}
                                inputMode={
                                  param.type === "integer" ||
                                  param.type === "number"
                                    ? "decimal"
                                    : "text"
                                }
                                placeholder={
                                  param.default !== null &&
                                  param.default !== undefined
                                    ? String(param.default)
                                    : undefined
                                }
                                onChange={(e) =>
                                  updateRewardParam(
                                    reward.id,
                                    param.name,
                                    sanitizeParamInput(param.type, e.target.value),
                                  )
                                }
                                data-testid={`challenge-card-reward-param-${reward.id}-${param.name}`}
                              />
                            </label>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {/* One reward per distinct reward type — hide the "Add another
                  Reward" CTA once every type could be in play (MAX_REWARDS). */}
              {challenge.rewards.length < maxRewards && (
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={handleAddReward}
                  data-testid="challenge-card-add-reward"
                >
                  {AppStrings.ADD_ANOTHER_REWARD}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className={styles.tasksSection}>
          <span className={styles.sectionLabel}>
            {AppStrings.TASKS}
          </span>
          {challenge.tasks.length > 0 && (
            // Editing-state task list is always editable (the expanded
            // branch is unreachable in locked mode), so we always mount
            // a DndContext here. Same sensors / collision strategy /
            // handler as the collapsed branch — single source of truth
            // for the per-card task drag wiring.
            <div className={styles.taskListWrap}>
              <DndContext
                sensors={taskSensors}
                collisionDetection={closestCenter}
                onDragEnd={handleTaskDragEnd}
              >
                <SortableContext
                  items={taskIds}
                  strategy={verticalListSortingStrategy}
                >
                  <ul
                    className={`${styles.taskList} ${
                      isPersistingTaskReorder ? styles.taskListReordering : ""
                    }`.trim()}
                    data-testid="challenge-card-expanded-task-list"
                  >
                    {challenge.tasks.map((task) => (
                      <SortableExpandedTaskRow
                        key={task.id}
                        task={task}
                        onEdit={handleOpenEditTask}
                        onCopy={taskCopyHandler}
                      />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>
              {taskReorderOverlay}
            </div>
          )}
          <button
            type="button"
            className={`${styles.addTaskRow} ${
              isAddTaskDisabled ? styles.addTaskRowDisabled : ""
            }`.trim()}
            onClick={handleOpenCreateTask}
            disabled={isAddTaskDisabled}
            title={
              isAddTaskDisabled
                ? AppStrings.ADD_TASK_REQUIRES_SAVE
                : undefined
            }
            data-testid="challenge-card-add-task"
          >
            <PlusOutlined className={styles.addTaskIcon} aria-hidden="true" />
            <span>{AppStrings.ADD_TASK}</span>
          </button>
          {canPasteTask && (
            <div
              className={styles.pasteCard}
              data-testid="challenge-card-paste-section"
            >
              <div className={styles.pasteInfo}>
                <SnippetsOutlined
                  className={styles.pasteIcon}
                  aria-hidden="true"
                />
                <div className={styles.pasteText}>
                  <span
                    className={styles.pasteTitle}
                    data-testid="challenge-card-paste-task-name"
                  >
                    {AppStrings.PASTE_TASK_CLIPBOARD_LABEL}:{" "}
                    {`"${
                      copiedTask?.name?.trim() ||
                      AppStrings.PASTE_TASK_UNTITLED
                    }"`}
                  </span>
                  <span className={styles.pasteSubtitle}>
                    {AppStrings.PASTE_TASK_HINT}
                  </span>
                </div>
              </div>
              <div className={styles.pasteActions}>
                <Button
                  intent="primary"
                  onClick={handlePasteTask}
                  disabled={isAddTaskDisabled || createTaskMutation.isPending}
                  loading={createTaskMutation.isPending}
                  title={
                    isAddTaskDisabled
                      ? AppStrings.ADD_TASK_REQUIRES_SAVE
                      : undefined
                  }
                  className={styles.pasteButton}
                  data-testid="challenge-card-paste-task"
                >
                  {AppStrings.PASTE_TASK}
                </Button>
                {onClearClipboard && (
                  <CardIconButton
                    ariaLabel={AppStrings.PASTE_TASK_CLEAR}
                    onClick={onClearClipboard}
                    testId="challenge-card-clear-clipboard"
                  >
                    <CloseOutlined />
                  </CardIconButton>
                )}
              </div>
            </div>
          )}
          {isAddTaskDisabled && (
            <span
              className={styles.addTaskHint}
              data-testid="challenge-card-add-task-hint"
            >
              {AppStrings.ADD_TASK_REQUIRES_SAVE}
            </span>
          )}
        </div>

        <div className={styles.cardFooter}>
          {missingRequirementLabels.length > 0 && (
            <p
              className={styles.requiredHint}
              data-testid="challenge-card-missing-requirements"
            >
              {AppStrings.CREATE_WIZARD_REQUIRED_FIELDS_HINT}{" "}
              {missingRequirementLabels.join(", ")}
            </p>
          )}
          {challenge.isSaved && (
            <Button
              intent="danger"
              variant="outline"
              className={styles.deleteButton}
              onClick={handleRequestDelete}
              data-testid="challenge-card-delete"
            >
              {AppStrings.CHALLENGE_DELETE}
            </Button>
          )}
          <Button
            intent="primary"
            onClick={handleSave}
            // Block save until the challenge has a name and at least one
            // complete reward (type + positive numeric amount) — same
            // disabled-until-valid contract the wizard's Next CTA uses.
            disabled={
              !isChallengeValid(challenge) ||
              missingRewardParamLabels.length > 0 ||
              createChallengeMutation.isPending ||
              updateChallengeMutation.isPending ||
              updateChallengeRewardMutation.isPending
            }
            loading={
              createChallengeMutation.isPending ||
              updateChallengeMutation.isPending ||
              updateChallengeRewardMutation.isPending
            }
            data-testid="challenge-card-save"
          >
            {AppStrings.CHALLENGE_SAVE}
          </Button>
        </div>
      </article>

      <CreateTaskModal
        open={isTaskModalOpen}
        taskIndex={nextTaskIndex}
        initialValues={editingTask}
        forcedXpAmount={forcedXpAmount}
        defaultSubmissionModes={defaultSubmissionModes}
        {...taskModalOptionProps}
        onClose={handleCloseTaskModal}
        onSubmit={handleTaskSubmit}
        onDelete={editingTask ? handleRequestDeleteTask : undefined}
        // Either mutation can be in flight depending on whether the
        // modal is in create or edit mode. Disabling on both keeps the
        // submit button accurate regardless of which path the admin
        // hit, and prevents a double-fire if React Query re-renders
        // mid-flight.
        isSubmitting={
          createTaskMutation.isPending || updateTaskMutation.isPending
        }
      />

      {viewTaskModal}

      <ConfirmDeleteTaskModal
        open={isTaskDeleteConfirmOpen}
        taskName={editingTask?.name ?? ""}
        onCancel={handleCancelDeleteTask}
        onConfirm={handleConfirmDeleteTask}
        isDeleting={deleteTaskMutation.isPending}
      />

      <ConfirmDeleteChallengeModal
        open={isDeleteConfirmOpen}
        challengeName={challenge.name}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteChallengeMutation.isPending}
      />
    </>
  );
};

// ─── Local helpers ────────────────────────────────────────────────────────

type CardIconButtonProps = {
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
  disabled?: boolean;
};

const CardIconButton: FC<CardIconButtonProps> = ({
  ariaLabel,
  onClick,
  children,
  testId,
  disabled,
}) => (
  <button
    type="button"
    className={styles.iconButton}
    onClick={onClick}
    aria-label={ariaLabel}
    disabled={disabled}
    data-testid={testId}
  >
    {children}
  </button>
);

/** Renders the icon thumbnail in the collapsed state.
 *
 *  Owns the lifetime of its `URL.createObjectURL` blob, generating it inside
 *  `useEffect` and revoking via the same effect's cleanup. This is the
 *  React-18-StrictMode-safe pattern: in dev StrictMode mounts → unmounts →
 *  re-mounts the effect, so the cleanup runs once between mounts and revokes
 *  the URL. A `useMemo` would not re-run on that second mount (its deps
 *  haven't changed) and the `<img>` would be left pointing at a revoked URL,
 *  producing the browser's broken-image icon. Using `useState` instead
 *  forces a fresh URL on every effect run.
 */
const ChallengeIconThumb: FC<{
  file?: File;
  imgKey?: string | null;
  imageUrl?: string | null;
}> = ({ file, imgKey, imageUrl }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const blobUrl = URL.createObjectURL(file);
    setUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [file]);

  // Falls back to the persisted remote image (locked / collapsed views load
  // challenges from the API with no local `File`). Prefer the BE-provided
  // `image_url` and only reconstruct from `imgKey` when it's missing. A failed
  // remote load hides the thumb rather than showing a broken image.
  const [remoteErrored, setRemoteErrored] = useState(false);
  useEffect(() => {
    setRemoteErrored(false);
  }, [imageUrl, imgKey]);

  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={styles.iconThumb}
        data-testid="challenge-card-icon-thumb"
      />
    );
  }

  const remoteUrl = remoteErrored
    ? null
    : (imageUrl ?? buildGamePassImageUrl(imgKey));
  if (remoteUrl) {
    return (
      <img
        src={remoteUrl}
        alt=""
        className={styles.iconThumb}
        onError={() => setRemoteErrored(true)}
        data-testid="challenge-card-icon-thumb"
      />
    );
  }

  return null;
};

/** Renders the saved-task thumbnail in the editing-state task list. Falls
 *  back to a neutral placeholder square when no image was uploaded so the
 *  row layout stays grid-aligned.
 *
 *  Owns its own `URL.createObjectURL` lifecycle — both the create and the
 *  revoke happen inside the same `useEffect` so the cleanup always pairs
 *  with the URL the effect itself produced. Critical under React 18
 *  StrictMode (dev), which mounts → unmounts → re-mounts effects: a
 *  `useMemo`-based URL would survive across that cycle, get revoked by
 *  the first cleanup, and never be regenerated — leaving the `<img>` with
 *  a dead `src` and rendering as a broken image. */
const TaskItemImage: FC<{
  file?: File;
  imgKey?: string | null;
  imageUrl?: string | null;
}> = ({ file, imgKey, imageUrl }) => {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setUrl(null);
      return undefined;
    }
    const blobUrl = URL.createObjectURL(file);
    setUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [file]);

  // Falls back to the persisted remote image (locked / collapsed views load
  // tasks from the API with no local `File`). Prefer the BE-provided
  // `image_url` and only reconstruct from `imgKey` when it's missing. A failed
  // remote load drops back to the neutral placeholder square.
  const [remoteErrored, setRemoteErrored] = useState(false);
  useEffect(() => {
    setRemoteErrored(false);
  }, [imageUrl, imgKey]);

  const src =
    url ??
    (remoteErrored ? null : (imageUrl ?? buildGamePassImageUrl(imgKey)));
  if (!src) {
    return (
      <span
        className={styles.taskImageFallback}
        data-testid="challenge-card-task-image-fallback"
        aria-hidden="true"
      />
    );
  }
  return (
    <img
      src={src}
      alt={AppStrings.CHALLENGE_TASK_IMAGE_ALT}
      className={styles.taskImage}
      onError={url ? undefined : () => setRemoteErrored(true)}
      data-testid="challenge-card-task-image"
    />
  );
};

/**
 * Drag-and-drop bindings supplied to a task row by a parent that owns a
 * `@dnd-kit/sortable` `SortableContext`. Mirrors the public
 * `ChallengeCardDragProps` contract but typed for an `<li>` row + its
 * native-`<button>` drag handle.
 *
 * Optional everywhere — locked-mode cards render the same row markup
 * with `drag={undefined}` so the handle stays inert. Keeping the row
 * components dnd-agnostic means they can be reused inside a future
 * surface (e.g. a `<ChallengeCard>` mounted in a read-only preview)
 * without any plumbing.
 */
type TaskRowDragProps = {
  setNodeRef: Ref<HTMLLIElement>;
  setActivatorNodeRef: Ref<HTMLButtonElement>;
  attributes: HTMLAttributes<HTMLLIElement>;
  listeners: HTMLAttributes<HTMLElement> | undefined;
  transformStyle: CSSProperties;
  isDragging: boolean;
};

/** Pure row used by the collapsed-state task list. Renders the same
 *  thumbnail + name + description + reward chip + eye icon as before.
 *  When `drag` is supplied the row becomes the sortable node and its
 *  handle the activator; when it's omitted (locked mode), the handle
 *  stays disabled and the row is plain. */
const CollapsedTaskRow: FC<{
  task: TaskDraft;
  onView: (id: string) => void;
  /** When provided, a Copy icon renders that puts this task on the editor
   *  clipboard. Omitted (locked mode / flag off) hides the icon. */
  onCopy?: (task: TaskDraft) => void;
  drag?: TaskRowDragProps;
}> = ({ task, onView, onCopy, drag }) => {
  const taskRewardChips = task.rewards.filter((r) => r.amount || r.type);
  return (
    <li
      ref={drag?.setNodeRef}
      className={`${styles.collapsedTaskItem} ${
        drag?.isDragging ? styles.taskRowDragging : ""
      }`.trim()}
      style={drag?.transformStyle}
      {...(drag?.attributes ?? {})}
      data-testid={`challenge-card-collapsed-task-${task.id}`}
    >
      <button
        type="button"
        ref={drag?.setActivatorNodeRef}
        className={`${styles.taskDragHandle} ${
          drag ? styles.dragHandleActive : ""
        }`.trim()}
        aria-label={AppStrings.CHALLENGE_TASK_DRAG_HANDLE}
        data-testid={`challenge-card-collapsed-task-drag-${task.id}`}
        disabled={!drag}
        {...(drag?.listeners ?? {})}
      >
        <HolderOutlined aria-hidden="true" />
      </button>
      <TaskItemImage
        file={task.imageFile}
        imgKey={task.imgKey}
        imageUrl={task.imageUrl}
      />
      <div className={styles.collapsedTaskMain}>
        <span className={styles.collapsedTaskName}>{task.name}</span>
        {task.description && (
          <span className={styles.collapsedTaskDescription}>
            {task.description}
          </span>
        )}
        {taskRewardChips.length > 0 && (
          <div
            className={styles.collapsedTaskRewards}
            data-testid={`challenge-card-collapsed-task-rewards-${task.id}`}
          >
            {taskRewardChips.map((reward) => (
              <StatusBadge
                key={reward.id}
                tone="warning"
                variant="soft"
                size="sm"
              >
                {formatRewardChip(reward)}
              </StatusBadge>
            ))}
          </div>
        )}
      </div>
      <div className={styles.taskActions}>
        <CardIconButton
          ariaLabel={AppStrings.CHALLENGE_TASK_VIEW}
          onClick={() => onView(task.id)}
          testId={`challenge-card-collapsed-task-view-${task.id}`}
        >
          <EyeOutlined />
        </CardIconButton>
        {onCopy && (
          <CardIconButton
            ariaLabel={AppStrings.CHALLENGE_TASK_COPY}
            onClick={() => onCopy(task)}
            testId={`challenge-card-collapsed-task-copy-${task.id}`}
          >
            <CopyOutlined />
          </CardIconButton>
        )}
      </div>
    </li>
  );
};

/** Sortable adapter for `CollapsedTaskRow`. Lives outside the parent
 *  component's `.map()` because `useSortable` is a hook and the Rules of
 *  Hooks forbid calling it inside a loop. */
const SortableCollapsedTaskRow: FC<{
  task: TaskDraft;
  onView: (id: string) => void;
  onCopy?: (task: TaskDraft) => void;
}> = ({ task, onView, onCopy }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });
  const drag: TaskRowDragProps = {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    transformStyle: {
      transform: CSS.Transform.toString(transform),
      transition,
    },
    isDragging,
  };
  return (
    <CollapsedTaskRow task={task} onView={onView} onCopy={onCopy} drag={drag} />
  );
};

/**
 * Sortable row for the editing-state task list. Unlike
 * `CollapsedTaskRow`, this row has no inert counterpart — the editing
 * surface only renders when the card is unlocked, so a `drag` bundle
 * is always present. Inlining the markup here keeps the component
 * surface lean and avoids carrying defensive optional-chain branches
 * for a code path that's unreachable in production.
 */
const SortableExpandedTaskRow: FC<{
  task: TaskDraft;
  onEdit: (id: string) => void;
  onCopy?: (task: TaskDraft) => void;
}> = ({ task, onEdit, onCopy }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });
  const transformStyle: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <li
      ref={setNodeRef}
      className={`${styles.taskItem} ${
        isDragging ? styles.taskRowDragging : ""
      }`.trim()}
      style={transformStyle}
      {...attributes}
      data-testid={`challenge-card-task-${task.id}`}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className={`${styles.taskDragHandle} ${styles.dragHandleActive}`}
        aria-label={AppStrings.CHALLENGE_TASK_DRAG_HANDLE}
        data-testid={`challenge-card-task-drag-${task.id}`}
        {...(listeners ?? {})}
      >
        <HolderOutlined aria-hidden="true" />
      </button>
      <TaskItemImage
        file={task.imageFile}
        imgKey={task.imgKey}
        imageUrl={task.imageUrl}
      />
      <div className={styles.taskItemMain}>
        <span className={styles.taskName}>{task.name}</span>
        {task.description && (
          <span className={styles.taskDescription}>{task.description}</span>
        )}
      </div>
      <div className={styles.taskActions}>
        {onCopy && (
          <CardIconButton
            ariaLabel={AppStrings.CHALLENGE_TASK_COPY}
            onClick={() => onCopy(task)}
            testId={`challenge-card-task-copy-${task.id}`}
          >
            <CopyOutlined />
          </CardIconButton>
        )}
        <CardIconButton
          ariaLabel={AppStrings.CHALLENGE_EDIT_TASK}
          onClick={() => onEdit(task.id)}
          testId={`challenge-card-task-edit-${task.id}`}
        >
          <EditOutlined />
        </CardIconButton>
      </div>
    </li>
  );
};

const REWARD_LABEL_BY_TYPE: Record<RewardType, string> = {
  FREE_CASH_PICK: AppStrings.REWARD_TYPE_FREE_CASH_PICK,
  PROFIT_BOOST: AppStrings.REWARD_TYPE_PROFIT_BOOST,
  REBET_CASH: AppStrings.REWARD_TYPE_REBET_CASH,
  REBET_COINS: AppStrings.REWARD_TYPE_REBET_COINS,
  XP: AppStrings.REWARD_TYPE_XP,
  USD: AppStrings.REWARD_TYPE_USD,
  NIRA: AppStrings.REWARD_TYPE_NIRA,
};

/** Reward types that render with a `$` currency prefix in the chip view.
 *  Kept as a typed Set so adding a future cash-denominated reward type is a
 *  one-line change (extend the set + extend the `RewardType` union) and the
 *  `formatRewardChip` branching logic doesn't have to grow another `if`. */
const CASH_REWARD_TYPES: ReadonlySet<RewardType> = new Set<RewardType>([
  "FREE_CASH_PICK",
  "REBET_CASH",
  "USD",
]);

/** The reward types that carry a static (non-config) label + chip treatment. */
const isKnownRewardType = (type: string): type is RewardType =>
  Object.prototype.hasOwnProperty.call(REWARD_LABEL_BY_TYPE, type);

/** Resolve a reward row's display label, preferring the live config label
 *  (currency + bonus rewards) and falling back to the static catalogue for the
 *  known play/value/XP types (used by task chips, which carry no config map). */
const resolveRewardLabel = (
  reward: RewardDraft,
  labelByValue?: Map<string, string>,
): string => {
  if (reward.type && labelByValue?.has(reward.type)) {
    return labelByValue.get(reward.type) ?? "";
  }
  if (reward.type && isKnownRewardType(reward.type)) {
    return REWARD_LABEL_BY_TYPE[reward.type];
  }
  return reward.type ?? "";
};

/** Formats a reward chip for the summary (collapsed) view. The shape mirrors
 *  the Figma exactly:
 *    FREE_CASH_PICK → `$50 Free Cash Pick` (currency prefix)
 *    REBET_CASH     → `$25 Rebet Cash`     (currency prefix)
 *    USD            → `$10 USD`            (currency prefix)
 *    XP             → `500xp`              (compact "xp" suffix, no label word)
 *    bonus / other  → `{amount} {label}`   (e.g. `25 Free Cash Pick`)
 *
 *  Bonus rewards (config-driven) always render as `{amount} {label}` — the
 *  `$` / `xp` special-casing is reserved for the fixed currency types. Partial
 *  states (amount-only / type-only) fall through to a best-effort display so an
 *  in-flight reward draft doesn't render as an empty chip. */
const formatRewardChip = (
  reward: RewardDraft,
  labelByValue?: Map<string, string>,
): string => {
  const amount = reward.amount?.trim() ?? "";
  const label = resolveRewardLabel(reward, labelByValue);
  const isBonus = Boolean(reward.bonusRewardType);
  if (!isBonus && reward.type && CASH_REWARD_TYPES.has(reward.type as RewardType)) {
    if (!amount) return label;
    return `$${amount} ${label}`;
  }
  if (!isBonus && reward.type === "XP") {
    if (!amount) return REWARD_LABEL_BY_TYPE.XP;
    return `${amount}xp`;
  }
  if (amount && label) return `${amount} ${label}`;
  return amount || label;
};

export default ChallengeCard;

// Re-export so tests that need a fresh task can build one without dragging
// in the full data module.
export { createEmptyTask };
